// import node_modules
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@storylinerlabs/design';
import { useImmer } from 'use-immer';

// import styles
import styles from './StickyFeatureNav.module.css';
import classNames from 'classnames';

// define component
const StickyFeatureNav: React.FunctionComponent = () => {
  // init hooks
  const { t } = useTranslation('features');

  // init states
  const [isStickyState, setIsStickyState] = useImmer<boolean>(false);
  const [visibleSection, setVisibleSection] = useImmer<string>('');

  // init refs
  const headerRef = React.createRef<HTMLDivElement>();

  // init vars
  const sections: { id: string; label: string }[] = React.useMemo(
    () => t('features', { returnObjects: true }),
    [t],
  );

  // init func
  const onGetDimensions = React.useCallback((element: HTMLElement) => {
    const { height } = element?.getBoundingClientRect() || {};
    const offsetTop = element?.offsetTop;
    const offsetBottom = offsetTop + height;

    return { height, offsetTop, offsetBottom };
  }, []);

  const onScrollTo = React.useCallback((sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 56; // sticky nav height
      window.scroll({
        top: element.offsetTop - offset,
        left: 0,
        behavior: 'smooth',
      });
    }
  }, []);

  const onHandleScroll = React.useCallback(() => {
    // get vars
    const { height: headerHeight } = onGetDimensions(headerRef.current as HTMLElement);
    const scrollPosition = window.scrollY + headerHeight + headerHeight + headerHeight;

    // find selected section
    const selected = sections?.find(({ id: sectionId }) => {
      const element = document.getElementById(sectionId);
      if (element) {
        const { offsetBottom, offsetTop } = onGetDimensions(element);
        return scrollPosition > offsetTop && scrollPosition < offsetBottom + headerHeight;
      }
      return false;
    });

    // update buttons while scrolling
    if (selected && selected.id !== visibleSection) {
      setVisibleSection(selected.id);
      setIsStickyState(true);

      // horizontally scroll button into view
      const navbar = document.getElementById('feature-nav');
      const button = document.getElementById(`button-${selected.id}`);
      if (navbar && button) {
        const { left, width } = button.getBoundingClientRect();
        if (left > window.innerWidth - width) {
          navbar.scrollLeft = left;
        } else if (left - width < 0) {
          navbar.scrollLeft = left;
        }
      }

      // hide when above or below sections
    } else if (!selected && visibleSection) {
      // update state
      setVisibleSection('');
      setIsStickyState(false);
    }
  }, [headerRef, onGetDimensions, sections, setVisibleSection, setIsStickyState, visibleSection]);

  // handle data changes
  React.useEffect(() => {
    // init scroll
    // https://codedaily.io/tutorials/Sticky-Header-with-Highlighting-Sections-on-Scroll
    onHandleScroll();

    // listen to scroll changes
    window.addEventListener('scroll', onHandleScroll);

    // unubscribe on unmount
    return () => {
      window.removeEventListener('scroll', onHandleScroll);
    };
  }, [onHandleScroll]);

  // init render
  return (
    <nav
      className={classNames(styles['navigation'], isStickyState && styles['is-sticky'])}
      ref={headerRef}
      id="feature-nav"
    >
      <div className={styles['container']}>
        {sections.map((section) => (
          <Button
            buttonId={`button-${section.id}`}
            color={visibleSection === section.id ? 'ultraLight' : 'naked'}
            key={section.id}
            onClick={() => onScrollTo(section.id)}
            text={section.label}
          />
        ))}
      </div>
    </nav>
  );
};

export default StickyFeatureNav;
